import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, RefreshCw, ArrowLeft, AlertTriangle } from 'lucide-react';
import { authAPI } from '../services/api';
import { generarPasswordSeguro } from '../utils/helpers';
import { Button, Input, Card } from './ui';
import logoDefault from '../assets/almaquinta.svg';

/**
 * Pagina para consumir el enlace del correo: /reset-password?token=xxx
 * Valida el token al montar. Si es invalido/expirado/usado, muestra mensaje.
 * Si es valido, muestra 2 inputs (nueva + confirmar) + ojito + boton generar.
 */
const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [checkingToken, setCheckingToken] = useState(true);
    const [tokenValido, setTokenValido] = useState(false);
    const [tokenReason, setTokenReason] = useState(''); // 'expired' | 'used' | ''

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setCheckingToken(false);
            setTokenValido(false);
            return;
        }
        authAPI.validateResetToken(token)
            .then(r => {
                setTokenValido(!!r.valid);
                setTokenReason(r.reason || '');
            })
            .catch(() => setTokenValido(false))
            .finally(() => setCheckingToken(false));
    }, [token]);

    const handleGenerar = () => {
        const generada = generarPasswordSeguro(14);
        setPassword(generada);
        setConfirm(generada);
        setShowPass(true);
        setShowConfirm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            return;
        }
        if (password !== confirm) {
            setError('Las contraseñas no coinciden');
            return;
        }
        setSaving(true);
        try {
            await authAPI.resetPassword(token, password);
            toast.success('Contraseña actualizada. Inicia sesión con tu nueva contraseña.');
            navigate('/login', { replace: true });
        } catch (err) {
            setError(err.message || 'No pudimos restablecer la contraseña');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-bg">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="hidden dark:block absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl" />
                <div className="hidden dark:block absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-purple2/10 blur-3xl" />
            </div>

            <Card className="relative w-full max-w-md">
                <Card.Body className="p-8 sm:p-10">
                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-20 h-20 rounded-2xl bg-white/95 p-2 shadow-soft mb-4 inline-flex items-center justify-center">
                            <img src={logoDefault} alt="Don Presupuesto" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-2xl font-bold text-text">Nueva contraseña</h1>
                        <p className="text-sm text-muted mt-1">
                            {checkingToken ? 'Validando enlace...' : (tokenValido ? 'Elige una contraseña segura' : 'Enlace no válido')}
                        </p>
                    </div>

                    {checkingToken && (
                        <div className="text-center text-sm text-muted">Validando enlace…</div>
                    )}

                    {!checkingToken && !tokenValido && (
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center">
                                <AlertTriangle size={28} className="text-danger" />
                            </div>
                            <p className="text-sm text-center text-text">
                                {tokenReason === 'expired'
                                    ? 'El enlace ya expiró. Solicita uno nuevo.'
                                    : tokenReason === 'used'
                                    ? 'Este enlace ya fue usado. Solicita uno nuevo si lo necesitas.'
                                    : 'El enlace es inválido o ya no existe.'}
                            </p>
                            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                                Solicitar un nuevo enlace
                            </Link>
                            <Link
                                to="/login"
                                className="text-sm text-muted hover:text-text inline-flex items-center gap-1"
                            >
                                <ArrowLeft size={14} /> Volver al login
                            </Link>
                        </div>
                    )}

                    {!checkingToken && tokenValido && (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            {error && (
                                <div className="px-3 py-2.5 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                                    {error}
                                </div>
                            )}

                            <Input
                                type={showPass ? 'text' : 'password'}
                                label="Nueva contraseña"
                                placeholder="Mínimo 8 caracteres"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoFocus
                                prefix={<Lock size={16} />}
                                suffix={
                                    <button
                                        type="button"
                                        onClick={() => setShowPass(v => !v)}
                                        tabIndex={-1}
                                        className="text-muted hover:text-text transition"
                                        aria-label={showPass ? 'Ocultar' : 'Mostrar'}
                                    >
                                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                }
                            />

                            <Input
                                type={showConfirm ? 'text' : 'password'}
                                label="Confirmar contraseña"
                                placeholder="Repite la contraseña"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                                prefix={<Lock size={16} />}
                                suffix={
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(v => !v)}
                                        tabIndex={-1}
                                        className="text-muted hover:text-text transition"
                                        aria-label={showConfirm ? 'Ocultar' : 'Mostrar'}
                                    >
                                        {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                }
                            />

                            <Button
                                type="button"
                                variant="soft"
                                size="sm"
                                leftIcon={<RefreshCw size={14} />}
                                onClick={handleGenerar}
                            >
                                Generar contraseña segura
                            </Button>

                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                fullWidth
                                disabled={saving || !password || !confirm}
                                className="mt-2"
                            >
                                {saving ? 'Guardando...' : 'Restablecer contraseña'}
                            </Button>

                            <Link
                                to="/login"
                                className="text-sm text-muted hover:text-text inline-flex items-center gap-1 justify-center mt-2"
                            >
                                <ArrowLeft size={14} /> Volver al login
                            </Link>
                        </form>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default ResetPassword;
